import each from 'lodash/each';
import pick from 'lodash/pick';
import toLower from 'lodash/toLower';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import {
  VAT_RATE,
  MARGIN_RATE,
  QUOTE_PRICE_COLUMNS,
  ADJUSTMENT_TYPE,
  DRIVER_FEE,
  COLLECTION_AND_DELIVERY_TYPE,
  ADDITIONAL_DISCOUNT_TYPE,
} from './constants';
import { applyVat, penceToPounds, poundsToPence } from '$app/utils/util';

const applyRate = (price, rate) => parseFloat((price * rate).toFixed(2));
export const applyVAT = (price) => applyRate(price, VAT_RATE);
export const removeVAT = (price) => applyRate(price, 1 / VAT_RATE);
export const applyMargin = (price) => applyRate(price, MARGIN_RATE);

/*
 * Warning!
 **********
 * TODO: This should be moved to our backend!!
 *
 * This kind of implicit type checking is leading to lots if bugs
 * https://docs.google.com/document/d/1nZ9CpyS6Ta9TtxN_9xMToN_q-yZXZg3zbXguSTKbvxw/edit?usp=sharing
 **
 */

export const getAccountCodeByType = (type) => {
  switch (type) {
    case 'mot':
      return '201';
    case 'service':
      return '202';
    default:
      return '203';
  }
};

const GARAGE_COLLECTION_DELIVERY = toLower('Garage C&D');

export const isGarageCollectionDelivery = (wo) =>
  wo &&
  (toLower(get(wo, 'workSummary', '')).includes(GARAGE_COLLECTION_DELIVERY) ||
    toLower(get(wo, 'title', '')).includes(GARAGE_COLLECTION_DELIVERY) ||
    toLower(get(wo, 'accountCode', '')) === '315');

const FIXTER_COLLECTION_DELIVERY = toLower('Fixter Collection & Delivery');

export const isDriverCollectionDelivery = (wo) =>
  wo &&
  (toLower(get(wo, 'workSummary', '')).includes(FIXTER_COLLECTION_DELIVERY) ||
    toLower(get(wo, 'title', '')).includes(FIXTER_COLLECTION_DELIVERY) ||
    toLower(get(wo, 'accountCode', '')) === '206' ||
    toLower(get(wo, 'source', '')) === toLower('PREMIUM_COLLECTION'));

/*
 * Warning!
 **********
 * TODO: This should be moved to our backend!!
 *
 * This kind of implicit type checking is leading to lots if bugs
 * https://docs.google.com/document/d/1nZ9CpyS6Ta9TtxN_9xMToN_q-yZXZg3zbXguSTKbvxw/edit?usp=sharing
 **
 */

export const getAccountingCodesByType = (type, wo) => {
  if (!type) throw new Error(`${wo.workSummary}, does not have an Accounting Code`);
  switch (type.toLowerCase()) {
    case 'mot':
      return {
        accountCode: '201',
        accountCodePurchases: 311,
        itemCode: 'MOT Purchase',
      };
    case 'service':
      return {
        accountCode: '202',
        accountCodePurchases: 312,
        itemCode: 'Periodic Service',
      };
    case 'other':
      return {
        accountCode: '203',
        accountCodePurchases: 313,
        itemCode: 'Mech Repair (Cost)',
      };
    case DRIVER_FEE:
      return {
        accountCode: '211',
        accountCodePurchases: null,
        itemCode: 'Driver Tips',
      };
    case ADJUSTMENT_TYPE:
      return {
        accountCode: '303',
        accountCodePurchases: 307,
      };
    case COLLECTION_AND_DELIVERY_TYPE:
      /** Garage */
      if (isGarageCollectionDelivery(wo))
        return {
          accountCode: '315',
          accountCodePurchases: 315,
          itemCode: 'C&D',
        };
      /** Fixter - Happy Slot */
      if (isDriverCollectionDelivery(wo))
        return {
          accountCode: '206',
          accountCodePurchases: 315,
          itemCode: 'C&D',
        };
      break;
    case ADDITIONAL_DISCOUNT_TYPE:
      return {
        accountCode: '0',
        accountCodePurchases: null,
      };
    default:
      throw new Error(`Unrecognized line item type: ${type}`);
  }
};

export const getLineItemPrice = ({ partsPrice, labourCost }) => {
  if (partsPrice !== 0) return partsPrice;
  if (labourCost !== 0) return labourCost;
  return 0;
};

export const quotePriceColumnsToPounds = (quote) => {
  const q = { ...quote };
  each(pick(q, QUOTE_PRICE_COLUMNS), (v, k) => (q[k] = penceToPounds(v)));
  return q;
};

export const isLabour = (subLineItem) => {
  // all subline items have a parentId
  if (!subLineItem.parentId) return false;

  const lowerWorkSummary = (subLineItem.workSummary || '').toLowerCase();
  const lowerTitle = (subLineItem.title || '').toLowerCase();

  return (
    lowerWorkSummary.includes('labour') ||
    lowerWorkSummary.includes("main-d'œuvre") ||
    lowerTitle.includes('labour') ||
    lowerTitle.includes("main-d'œuvre") ||
    lowerWorkSummary.includes('pre-contrôle') ||
    lowerTitle.includes('pre-contrôle')
  );
};

export const getVehicleOilCapacity = (vehicle = {}) => {
  const capacity = Number(vehicle.engineOilCapacity || vehicle.oilCapacity || vehicle.engine_oil_capacity || 0);
  return capacity > 1 ? capacity : 0;
};

const ENGINE_OIL_EXCLUSIONS = [
  'filter',
  'brake',
  'gear',
  'transmission',
  'coolant',
  'adblue',
  'ad-blue',
  'ad blue',
  'washer',
  'power steer',
  'power-steer',
  'hydraulic',
  'differential',
];

export const isOilFluidLineItem = (item = {}) => {
  const summary = toLower(item.workSummary || item.shortName || item.title || '');
  if (!summary) return false;
  if (ENGINE_OIL_EXCLUSIONS.some((term) => summary.includes(term))) return false;
  return (
    summary.includes('engine oil') ||
    summary.includes('motor oil') ||
    summary.includes('huile moteur') ||
    /\boil\b/.test(summary)
  );
};

export const getPriceListSubLineItemType = (item = {}) =>
  item.labourTime || item.labourCost ? 'labour' : 'part';

export const getPriceListSubLineItemQuantity = (item = {}, oilCapacity = 0) => {
  const type = getPriceListSubLineItemType(item);
  if (type === 'labour') return item.labourTime || 0;

  const quantity = item.quantity || item.partsQuantity || 0;
  if (isOilFluidLineItem(item) && oilCapacity > 1 && (!quantity || quantity === 1)) {
    return oilCapacity;
  }
  return quantity;
};

export const getLineItemQuantity = (lineItem = {}) => {
  const { partsQuantity, labourTime } = lineItem;
  if (isLabour(lineItem)) return labourTime || 0;
  // Unrecognised labour names (Diagnostic, Fitting) have partsQuantity 0 and labourTime set.
  return partsQuantity || labourTime || 0;
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

export const getCustomerLabourRatePounds = (labourRateForCustomerPence) => {
  if (labourRateForCustomerPence && typeof labourRateForCustomerPence === 'object') {
    return Number(labourRateForCustomerPence.price || labourRateForCustomerPence.garageOPPrice || 0) / 100;
  }
  return Number(labourRateForCustomerPence || 0) / 100;
};

export const getCustomerLabourRatePence = (garage, garagePrice) => {
  const fromGaragePrice = get(garagePrice, 'labourRate');
  if (isNumber(fromGaragePrice) && fromGaragePrice > 0) {
    return poundsToPence(fromGaragePrice);
  }

  const labourRate = get(garage, 'operations.labourRate');
  if (isNumber(labourRate) && labourRate > 0) return labourRate;
  if (labourRate && isNumber(labourRate.price) && labourRate.price > 0) return labourRate.price;

  const operations = get(garage, 'operations');
  if (Array.isArray(operations)) {
    const labourOp = operations.find(
      (op) => op.garageOPId === 'Labour rate' || op.operationId === 'Labour rate' || op.slug === 'labourRate'
    );
    const price = labourOp && (labourOp.garageOPPrice || labourOp.price);
    if (isNumber(price) && price > 0) return price;
  }

  return 0;
};

export const customerPriceLooksLikeGaragePrice = (lineItem = {}) => {
  const customer = Number(lineItem.fixedCostWithVAT || 0);
  const garage = Number(lineItem.garageCostWithVat || 0);
  if (!customer) return true;
  if (!garage) return false;
  return Math.abs(customer - garage) < 0.01;
};

export const calculateCustomerLineItemPrices = (lineItem, customerLabourRatePounds) => {
  const quantity = Number(lineItem.quantity) || 0;
  const isLabourRow = lineItem.type === 'labour';
  const unitPrice = isLabourRow
    ? customerLabourRatePounds || Number(lineItem.price) || 0
    : Number(lineItem.price) || 0;
  const fixedCost = applyMargin(roundMoney(quantity * unitPrice));
  const fixedCostWithVAT = lineItem.vatExempt ? fixedCost : applyVAT(fixedCost);
  return {
    ...lineItem,
    fixedCost,
    fixedCostWithVAT,
  };
};

export const prepareSelectedWorkOperationPricing = (wo = {}, customerLabourRatePounds = 0) => {
  const { subLineItems = [], ...workOperation } = wo;
  const usedCalculatedChildPrices = subLineItems.some(customerPriceLooksLikeGaragePrice);
  const lineItems = subLineItems.map((item) =>
    customerPriceLooksLikeGaragePrice(item)
      ? calculateCustomerLineItemPrices(item, customerLabourRatePounds)
      : item
  );
  const hasQuoteEngineCustomerPrices = Number(wo.fixedCostWithVAT || 0) > 0 && !usedCalculatedChildPrices;
  const customerTotal = lineItems.reduce((sum, item) => sum + (Number(item.fixedCostWithVAT) || 0), 0);
  const customerNet = lineItems.reduce((sum, item) => sum + (Number(item.fixedCost) || 0), 0);

  if (!hasQuoteEngineCustomerPrices) {
    workOperation.fixedCost = customerNet;
    workOperation.fixedCostWithVAT = customerTotal;
  }

  return {
    lineItems,
    workOperation,
    hasQuoteEngineCustomerPrices,
    isMissingValues: Boolean(lineItems.find(({ garageCost }) => !garageCost)),
    hasGarageCost: Number(wo.garageCostWithVat || 0) > 0,
  };
};

export const mapPriceListSubLineItem = (item, parentPrice, oilCapacity = 0) => {
  const type = getPriceListSubLineItemType(item);
  const quantity = getPriceListSubLineItemQuantity(item, oilCapacity);
  const unitPrice = penceToPounds(item.labourCost || item.partsPrice || 0);
  const originalQuantity = type === 'labour' ? item.labourTime : item.partsQuantity || 0;
  const sourceQuantity = type === 'labour' ? originalQuantity : item.quantity || item.partsQuantity || 0;
  const appliedOilCapacity =
    type !== 'labour' &&
    isOilFluidLineItem(item) &&
    oilCapacity > 1 &&
    (!sourceQuantity || sourceQuantity === 1) &&
    quantity === oilCapacity;
  let garageCost = penceToPounds(item.garageCost);
  let garageCostWithVat = penceToPounds(item.garageCostWithVat);
  let fixedCost = penceToPounds(item.fixedCost);
  let fixedCostWithVAT = penceToPounds(item.fixedCostWithVat);

  if (quantity !== originalQuantity) {
    garageCost = roundMoney(quantity * unitPrice);
    garageCostWithVat = item.vatExempt ? garageCost : roundMoney(applyVat(garageCost));
  }

  if (appliedOilCapacity) {
    // A 1-unit customer total would look like quote-engine pricing against the scaled garage cost.
    fixedCost = 0;
    fixedCostWithVAT = 0;
  }

  return {
    ...item,
    predefinedId: parentPrice.predefinedId,
    fixedCost,
    fixedCostWithVAT,
    garageCostWithVat,
    garageCost,
    workSummary: item.shortName || item.workSummary,
    type,
    quantity,
    price: unitPrice,
    searchTerms: parentPrice.searchTerms,
    vatExempt: item.vatExempt,
    category: item.category || parentPrice.category,
  };
};

export const mapPriceListWorkOperation = (price, oilCapacity = 0) => {
  const subLineItems = (price.subLineItems || []).map((item) =>
    mapPriceListSubLineItem(item, price, oilCapacity)
  );
  const garageCost = subLineItems.length
    ? roundMoney(subLineItems.reduce((sum, item) => sum + (Number(item.garageCost) || 0), 0))
    : penceToPounds(price.garageCost);
  const garageCostWithVat = subLineItems.length
    ? roundMoney(subLineItems.reduce((sum, item) => sum + (Number(item.garageCostWithVat) || 0), 0))
    : penceToPounds(price.garageCostWithVat);

  return {
    predefinedId: price.predefinedId,
    sku: price.sku,
    supported: price.supported,
    workSummary: price.shortName || price.workSummary,
    text: price.shortName || price.workSummary,
    value: price.predefinedId,
    type: price.type === 'component' ? 'other' : price.type,
    level: [1],
    vatExempt: price.vatExempt,
    fixedCost: penceToPounds(price.fixedCost),
    fixedCostWithVAT: penceToPounds(price.fixedCostWithVat),
    garageCostWithVat,
    customerPriceConfidence: price.customerPriceConfidence,
    garageCost,
    searchTerms: price.searchTerms,
    category: price.category,
    status: price.status,
    accountCode: price.accountCode,
    accountCodePurchases: price.accountCodePurchases,
    itemCode: price.itemCode,
    subLineItems,
  };
};
